import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getTenantBrandingParams } from "../utils/tenantHost";
import publicInstitutionService from "../service/publicInstitution.service";

/** Consumer site (typogram.in / localhost without tenant): no gating from institution plan. */
export const TENANT_FULL_ACCESS = {
  practice: true,
  tests: true,
  courses: true,
  testPasses: true,
  typingLessons: true,
  steno: true,
  stenoLessons: true,
  playHub: true,
  blog: true,
  community: true,
  referral: true,
  socialFriends: true,
  studySchedule: true,
  studyBooks: true,
};

const CORE_WHILE_LOADING = {
  practice: true,
  tests: true,
  courses: true,
  testPasses: true,
  typingLessons: true,
  steno: false,
  stenoLessons: false,
  playHub: false,
  blog: false,
  community: false,
  referral: false,
  socialFriends: false,
  studySchedule: false,
  studyBooks: false,
};

const ALL_OFF = {
  practice: false,
  tests: false,
  courses: false,
  testPasses: false,
  typingLessons: false,
  steno: false,
  stenoLessons: false,
  playHub: false,
  blog: false,
  community: false,
  referral: false,
  socialFriends: false,
  studySchedule: false,
  studyBooks: false,
};

let _titleObserver = null;

function applyTenantBranding(branding, pathname) {
  if (!branding) return;
  const name = branding.brandName || branding.institutionName;
  if (name) {
    document.title = name;
    if (!_titleObserver) {
      _titleObserver = new MutationObserver(() => {
        const current = document.title;
        if (current.includes("Typogram") || current.includes("typogram")) {
          document.title = current
            .replace(/Typogram/gi, name)
            .replace(/typogram\.in/gi, name);
        }
      });
      const titleEl = document.querySelector("title");
      if (titleEl) {
        _titleObserver.observe(titleEl, { childList: true, characterData: true, subtree: true });
      }
    }
  }
  if (branding.faviconUrl) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = branding.faviconUrl;
  }
  // Inject CSS Variables for core components to use natively
  let varsEl = document.getElementById("tenant-css-vars");
  if (!varsEl) {
    varsEl = document.createElement("style");
    varsEl.id = "tenant-css-vars";
    document.head.appendChild(varsEl);
  }
  
  let cssVariables = ":root {\n";
  cssVariables += `  --brand-primary: ${branding.primaryColor || '#8b5cf6'};\n`;
  cssVariables += `  --brand-secondary: ${branding.secondaryColor || '#6366f1'};\n`;
  cssVariables += `  --brand-accent: ${branding.accentColor || '#f59e0b'};\n`;
  cssVariables += `  --auth-bg-gradient: linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(15, 15, 30, 0.95) 100%);\n`;
  cssVariables += `  --auth-border: rgba(139, 92, 246, 0.2);\n`;

  // Dashboard Fallbacks
  cssVariables += `  --dashboard-header-bg: rgba(255, 255, 255, 0.03);\n`;
  cssVariables += `  --dashboard-header-border: rgba(255, 255, 255, 0.1);\n`;
  cssVariables += `  --dashboard-card-bg: rgba(255, 255, 255, 0.03);\n`;
  cssVariables += `  --dashboard-card-border: rgba(255, 255, 255, 0.1);\n`;
  cssVariables += `  --dashboard-tab-active: var(--brand-primary, #8b5cf6);\n`;

  // Courses Fallbacks
  cssVariables += `  --course-card-bg: rgba(255, 255, 255, 0.03);\n`;
  cssVariables += `  --course-card-border: rgba(255, 255, 255, 0.1);\n`;
  cssVariables += `  --course-progress: var(--brand-primary, #8b5cf6);\n`;
  cssVariables += `  --course-btn-bg: var(--brand-primary, #8b5cf6);\n`;

  // Steno Fallbacks
  cssVariables += `  --steno-arena-bg: #1a1a3e;\n`;
  cssVariables += `  --steno-stroke-color: var(--brand-primary, #8b5cf6);\n`;
  cssVariables += `  --steno-highlight: var(--brand-accent, #f59e0b);\n`;
  cssVariables += `  --steno-text: #ffffff;\n`;

  // Practice Arena Fallbacks
  cssVariables += `  --practice-arena-bg: transparent;\n`;
  cssVariables += `  --practice-correct-word: #10b981;\n`;
  cssVariables += `  --practice-incorrect-word: #ef4444;\n`;
  cssVariables += `  --practice-current-word: var(--brand-primary, #8b5cf6);\n`;
  
  cssVariables += "}";
  varsEl.textContent = cssVariables;

  // Inject custom CSS if present
  let styleEl = document.getElementById("tenant-custom-css");
  if (branding.customCss && branding.customCss.trim()) {
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = "tenant-custom-css";
      document.head.appendChild(styleEl);
    }

    const rawCss = branding.customCss;
    if (rawCss.includes("/* --- GLOBAL --- */") || rawCss.includes("/* --- AUTH PAGES --- */")) {
      const globalMatch = rawCss.match(/\/\* --- GLOBAL ---\s*\*\/([\s\S]*?)(?=\/\* ---|$)/);
      const authMatch = rawCss.match(/\/\* --- AUTH PAGES ---\s*\*\/([\s\S]*?)(?=\/\* ---|$)/);
      const dashboardMatch = rawCss.match(/\/\* --- DASHBOARD ---\s*\*\/([\s\S]*?)(?=\/\* ---|$)/);
      const coursesMatch = rawCss.match(/\/\* --- COURSES ---\s*\*\/([\s\S]*?)(?=\/\* ---|$)/);
      const stenoMatch = rawCss.match(/\/\* --- STENO ---\s*\*\/([\s\S]*?)(?=\/\* ---|$)/);
      const practiceMatch = rawCss.match(/\/\* --- PRACTICE ARENA ---\s*\*\/([\s\S]*?)(?=\/\* ---|$)/);

      const globalCss = globalMatch ? globalMatch[1].trim() : "";
      const authCss = authMatch ? authMatch[1].trim() : "";
      const dashboardCss = dashboardMatch ? dashboardMatch[1].trim() : "";
      const coursesCss = coursesMatch ? coursesMatch[1].trim() : "";
      const stenoCss = stenoMatch ? stenoMatch[1].trim() : "";
      const practiceCss = practiceMatch ? practiceMatch[1].trim() : "";

      const path = (pathname || window.location.pathname || "/").toLowerCase();
      let activeCss = globalCss;

      if (path.includes("/login") || path.includes("/register") || path.includes("/forgot-password") || path.includes("/reset-password") || path.includes("/verify-otp")) {
        activeCss += "\n\n/* Page Specific Override: Login/Signup */\n" + authCss;
      } else if (path.includes("/typing-display") || path.includes("/typing-display-test") || path.includes("/lessons") || path.includes("/lesson/") || path.includes("/start-test") || path.includes("/learn")) {
        activeCss += "\n\n/* Page Specific Override: Typing Arena */\n" + practiceCss;
      } else if (path === "/" || path.includes("/home") || path.includes("/dashboard") || path.includes("/profile") || path.includes("/student") || path.includes("/institutional")) {
        activeCss += "\n\n/* Page Specific Override: Portal Dashboard */\n" + dashboardCss;
      } else if (path.includes("/courses") || path.includes("/course")) {
        activeCss += "\n\n/* Page Specific Override: Courses */\n" + coursesCss;
      } else if (path.includes("/steno")) {
        activeCss += "\n\n/* Page Specific Override: Steno */\n" + stenoCss;
      }

      styleEl.textContent = activeCss;
    } else {
      styleEl.textContent = rawCss;
    }
  } else if (styleEl) {
    styleEl.remove();
  }
}

const TenantEntitlementsContext = createContext({
  isTenantHost: false,
  loading: true,
  subscriptionPlan: null,
  subscriptionLapsed: false,
  features: TENANT_FULL_ACCESS,
  branding: null,
});

export function TenantEntitlementsProvider({ children }) {
  const location = useLocation();
  const paramsOnMount = typeof window !== "undefined" ? getTenantBrandingParams() : null;
  const [state, setState] = useState(() => {
    if (!paramsOnMount) {
      return {
        isTenantHost: false,
        loading: false,
        subscriptionPlan: null,
        subscriptionLapsed: false,
        features: TENANT_FULL_ACCESS,
        branding: null,
      };
    }
    return {
      isTenantHost: true,
      loading: true,
      subscriptionPlan: "CORE",
      subscriptionLapsed: false,
      features: CORE_WHILE_LOADING,
      branding: null,
    };
  });

  const cancelledRef = useRef(false);

  const fetchBranding = useCallback(async (isInitial = false) => {
    const params = getTenantBrandingParams();
    if (!params) {
      setState({
        isTenantHost: false,
        loading: false,
        subscriptionPlan: null,
        subscriptionLapsed: false,
        features: TENANT_FULL_ACCESS,
        branding: null,
      });
      return;
    }

    try {
      const res = await publicInstitutionService.getBranding(params);
      const ok = res.data?.success;
      const payload = res.data?.data;
      if (cancelledRef.current) return;
      if (ok && payload?.features) {
        const f = payload.features;
        const brandingData = {
          id: payload.id,
          institutionName: payload.institutionName,
          brandName: payload.brandName,
          subdomain: payload.subdomain,
          logoUrl: payload.logoUrl,
          faviconUrl: payload.faviconUrl,
          primaryColor: payload.primaryColor,
          secondaryColor: payload.secondaryColor,
          accentColor: payload.accentColor,
          footerText: payload.footerText,
          customCss: payload.customCss,
        };
        if (isInitial) applyTenantBranding(brandingData, window.location.pathname);
        setState({
          isTenantHost: true,
          loading: false,
          subscriptionPlan: payload.subscriptionPlan || "CORE",
          subscriptionLapsed: !!payload.subscriptionLapsed,
          features: {
            practice: !!f.practice,
            tests: !!f.tests,
            courses: !!f.courses,
            testPasses: !!f.testPasses,
            typingLessons: !!f.typingLessons,
            steno: !!f.steno,
            stenoLessons: !!f.stenoLessons,
            playHub: !!f.playHub,
            blog: !!f.blog,
            community: !!f.community,
            referral: !!f.referral,
            socialFriends: !!f.socialFriends,
            studySchedule: !!f.studySchedule,
          },
          branding: brandingData,
        });
      } else {
        setState({
          isTenantHost: true,
          loading: false,
          subscriptionPlan: null,
          subscriptionLapsed: true,
          features: ALL_OFF,
          branding: null,
        });
      }
    } catch {
      if (!cancelledRef.current) {
        setState({
          isTenantHost: true,
          loading: false,
          subscriptionPlan: null,
          subscriptionLapsed: true,
          features: ALL_OFF,
          branding: null,
        });
      }
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    fetchBranding(true);

    const onFocus = () => fetchBranding(false);
    window.addEventListener("focus", onFocus);

    return () => {
      cancelledRef.current = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchBranding]);

  useEffect(() => {
    if (state.branding) {
      applyTenantBranding(state.branding, location.pathname);
    }
  }, [state.branding, location.pathname]);

  const value = useMemo(() => state, [state]);
  return (
    <TenantEntitlementsContext.Provider value={value}>
      {children}
    </TenantEntitlementsContext.Provider>
  );
}

export function useTenantEntitlements() {
  return useContext(TenantEntitlementsContext);
}

/** Returns the institution brand name on tenant hosts, or "Typogram" on the consumer site. */
export function useBrandName() {
  const { isTenantHost, branding } = useContext(TenantEntitlementsContext);
  if (isTenantHost && branding) {
    return branding.brandName || branding.institutionName || "Typogram®";
  }
  return "Typogram®";
}
